/**
 * Utility to parse structured LLM JSON into clinical HTML for the TipTap editor.
 */

import { isLamaOrDama, DEFAULT_LAMA_DAMA_DECLARATION } from "../constants/dischargeTypes";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Format a date-ish value for display in clinical documents.
 *
 * The backend sends dates as ISO timestamps (e.g. "2026-06-03T00:00:00"); we
 * want just the calendar date ("03 Jun 2026"). The date part is read straight
 * from the string (not via `new Date`) so date-only values never shift across
 * timezones. Empty → "-"; anything unparseable is returned unchanged.
 */
export const formatClinicalDate = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  const str = String(val).trim();

  // ISO date or datetime: YYYY-MM-DD[THH:mm:ss...] — format from the string itself.
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, m, d] = iso;
    const mon = MONTHS[parseInt(m, 10) - 1];
    if (mon) return `${d} ${mon} ${y}`;
  }

  // Fallback: trust a valid parse, otherwise leave the value as-is.
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return `${String(parsed.getDate()).padStart(2, "0")} ${MONTHS[parsed.getMonth()]} ${parsed.getFullYear()}`;
  }
  return str;
};

export const cleanAgeGender = (rawAge, rawGender) => {
  const age = (rawAge != null && rawAge !== "")
    ? String(rawAge).replace(/\s*\/\s*(?:male|female|m|f|other|o)\b/i, "").trim()
    : "";
  const gender = (rawGender != null && rawGender !== "") ? String(rawGender).trim() : "";
  if (age && gender) return `${age} / ${gender}`;
  return age || gender || "-";
};

export const parseSummaryJsonToHtml = (jsonPayload) => {
  if (!jsonPayload || typeof jsonPayload !== "object") return "";

  const safeArray = (val) => (Array.isArray(val) ? val.filter(v => v !== null && v !== "") : []);
  const safeStr = (val) => (val !== null && val !== undefined && val !== "" ? String(val) : "-");
  // List items may arrive as plain strings or as objects (e.g. investigations as
  // { orderDetail: "..." }). Extract a readable string so they never render as "[object Object]".
  const itemText = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") {
      return String(val.orderDetail || val.detail || val.description || val.name || val.text || val.value || "");
    }
    return String(val);
  };

  let htmlString = `<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">`;

  // Hospital Details Header
  const hosp = jsonPayload.hospital_details || {};
  if (hosp.name || hosp.address || (hosp.contact && hosp.contact !== "N/A")) {
    htmlString += `<div style="text-align: center; margin-bottom: 15px;">
      ${hosp.name ? `<h1 style="margin: 0 0 4px 0; font-size: 20px; font-weight: bold; color: #1a365d;">${hosp.name}</h1>` : ""}
      ${hosp.address && hosp.address !== "Hospital Address" ? `<div style="font-size: 12px; color: #555; margin-bottom: 2px;">${hosp.address}</div>` : ""}
      ${hosp.contact && hosp.contact !== "N/A" ? `<div style="font-size: 12px; color: #555;">Contact: ${hosp.contact}</div>` : ""}
    </div>`;
  }

  const isLamaDamaCase = isLamaOrDama(jsonPayload.header?.discharge_type);
  const titleText =
    jsonPayload.document_type ||
    (isLamaDamaCase
      ? String(jsonPayload.header?.discharge_type).toUpperCase().includes("LAMA")
        ? "DISCHARGE SUMMARY (LAMA)"
        : "DISCHARGE SUMMARY (DAMA)"
      : "DISCHARGE SUMMARY");

  htmlString += `<h2 style="text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px;">${titleText}</h2>`;

  // 1. Header & Patient Details
  if (jsonPayload.header) {
    const h = jsonPayload.header;
    htmlString += `<table style="width: 100%; margin-top: 10px; margin-bottom: 20px; border-collapse: collapse;">
      <tr>
        <td style="width: 50%;"><strong>Patient Name:</strong> ${safeStr(h.name)}</td>
        <td style="width: 50%;"><strong>Patient ID:</strong> ${safeStr(h.patient_id)}</td>
      </tr>
      <tr>
        <td><strong>UHID:</strong> ${safeStr(h.uhid || h.patient_id)}</td>
        <td><strong>Consultant:</strong> ${safeStr(h.consultant_incharge)}</td>
      </tr>
      <tr>
        <td><strong>Age/Gender:</strong> ${cleanAgeGender(h.age, h.gender)}</td>
        <td><strong>Admission Date:</strong> ${formatClinicalDate(h.date_of_admission)}</td>
      </tr>
      <tr>
        <td><strong>Bed No:</strong> ${safeStr(h.bed_no)}</td>
        <td><strong>Discharge Date:</strong> ${formatClinicalDate(h.date_of_discharge)}</td>
      </tr>
      <tr>
        <td><strong>Category:</strong> ${safeStr(h.category)}</td>
        <td><strong>Discharge Type:</strong> ${safeStr(h.discharge_type)}</td>
      </tr>
    </table>`;

    if (h.discharge_details) {
      const dd = h.discharge_details;
      htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Discharge Details</h3>`;
      htmlString += `<p><strong>Condition at Discharge:</strong> ${safeStr(dd.condition_at_discharge)}</p>`;
      htmlString += `<p><strong>Mobility Status:</strong> ${safeStr(dd.mobility_status)}</p>`;
      htmlString += `<p><strong>Follow-up Required:</strong> ${safeStr(dd.follow_up_required)}</p>`;
      htmlString += `<p><strong>Remarks:</strong> ${safeStr(dd.remarks)}</p>`;
      
      if (dd.vital_signs) {
        htmlString += `<p><strong>Vital Signs:</strong> BP: ${safeStr(dd.vital_signs.bp)}, Pulse: ${safeStr(dd.vital_signs.pulse)}, Temp: ${safeStr(dd.vital_signs.temperature)}, SpO2: ${safeStr(dd.vital_signs.spo2)}</p>`;
      }
    }

    if (isLamaDamaCase) {
      const ldd = h.lama_dama_details || {};
      const typeLabel = String(h.discharge_type).toUpperCase().includes("LAMA")
        ? "LEFT AGAINST MEDICAL ADVICE (LAMA)"
        : "DISCHARGE AGAINST MEDICAL ADVICE (DAMA)";
      const reasonDisplay =
        ldd.reason === "Other" && ldd.reason_other
          ? ldd.reason_other
          : ldd.reason || "-";
      htmlString += `<div style="margin-top: 15px; margin-bottom: 20px; border: 2px solid #d32f2f; border-radius: 4px; background-color: #fff8f8; padding: 12px;">
        <div style="color: #d32f2f; font-weight: bold; font-size: 14px; text-transform: uppercase; margin-bottom: 8px;">
          ⚠ MEDICO-LEGAL DOCUMENTATION: ${typeLabel}
        </div>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 13px;">
          <tr>
            <td style="width: 30%; padding: 4px 0; vertical-align: top;"><strong>Reason:</strong></td>
            <td style="padding: 4px 0;">${safeStr(reasonDisplay)}</td>
          </tr>
          <tr>
            <td style="padding: 4px 0; vertical-align: top;"><strong>Informed Risk Counseling:</strong></td>
            <td style="padding: 4px 0;">${safeStr(ldd.risks_explained_notes)}</td>
          </tr>
          <tr>
            <td style="padding: 4px 0; vertical-align: top;"><strong>Counseled By:</strong></td>
            <td style="padding: 4px 0;">${safeStr(ldd.counseled_by || h.consultant_incharge)}</td>
          </tr>
          <tr>
            <td style="padding: 4px 0; vertical-align: top;"><strong>Surrogate / Signer:</strong></td>
            <td style="padding: 4px 0;">${safeStr(ldd.surrogate_name)} ${ldd.surrogate_relationship ? `(${ldd.surrogate_relationship})` : ""} ${ldd.surrogate_contact ? `• Contact: ${ldd.surrogate_contact}` : ""}</td>
          </tr>
          <tr>
            <td style="padding: 4px 0; vertical-align: top;"><strong>Condition at Departure:</strong></td>
            <td style="padding: 4px 0;">${safeStr(ldd.condition_at_departure)}</td>
          </tr>
          <tr>
            <td style="padding: 4px 0; vertical-align: top;"><strong>Emergency Instructions:</strong></td>
            <td style="padding: 4px 0;">${safeStr(ldd.emergency_instructions)}</td>
          </tr>
        </table>
        <div style="font-size: 11px; color: #555; background-color: #ffebee; padding: 8px; border-left: 3px solid #d32f2f; margin-top: 6px;">
          <strong>Declaration / Legal Waiver:</strong> ${safeStr(ldd.declaration_text || DEFAULT_LAMA_DAMA_DECLARATION)}
        </div>
      </div>`;
    }
  }

  // 2. Final Diagnosis
  if (jsonPayload.final_diagnosis) {
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Final Diagnosis</h3>`;
    const diags = safeArray(jsonPayload.final_diagnosis);
    if (diags.length) {
      htmlString += `<ul>${diags.map(d => `<li>${itemText(d)}</li>`).join("")}</ul>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }
  }

  // 3. Investigations
  if (jsonPayload.investigations) {
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Investigations</h3>`;
    const invs = safeArray(jsonPayload.investigations);
    if (invs.length) {
      htmlString += `<ul>${invs.map(i => `<li>${itemText(i)}</li>`).join("")}</ul>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }
  }

  // 4. History
  if (jsonPayload.history) {
    const hist = jsonPayload.history;
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">History</h3>`;
    htmlString += `<p><strong>Presenting Illness:</strong> ${safeStr(hist.history_of_present_illness)}</p>`;
    
    const complaints = safeArray(hist.presenting_complaints);
    htmlString += `<p><strong>Presenting Complaints:</strong> ${complaints.length ? complaints.join(", ") : "-"}</p>`;
    
    htmlString += `<p><strong>Allergies:</strong> ${safeStr(hist.allergies)}</p>`;
    htmlString += `<p><strong>Past Medical History:</strong> ${safeStr(hist.past_medical_history)}</p>`;
    htmlString += `<p><strong>Past Surgical History:</strong> ${safeStr(hist.past_surgical_history)}</p>`;
    htmlString += `<p><strong>Family History:</strong> ${safeStr(hist.family_history)}</p>`;
    
    if (hist.personal_history) {
      const ph = hist.personal_history;
      htmlString += `<p><strong>Personal History:</strong> Diet: ${safeStr(ph.diet)}, Smoking: ${safeStr(ph.smoking)}, Alcohol: ${safeStr(ph.alcohol)}</p>`;
    }
  }

  // 5. Treatment Given
  if (jsonPayload.treatment_given) {
    const tg = jsonPayload.treatment_given;
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Treatment Given</h3>`;
    
    htmlString += `<h4>Procedures</h4>`;
    const procs = safeArray(tg.procedures);
    if (procs.length) {
      htmlString += `<ul>${procs.map(p => `<li>${p}</li>`).join("")}</ul>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }

    htmlString += `<h4>Medications Administered</h4>`;
    const meds = safeArray(tg.medications_administered).filter(m => m && (m.name || m.drug_name || m.dose || m.start || m.period));
    if (meds.length) {
      htmlString += `<table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
        <tr style="background-color: #f2f2f2;">
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Name</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Dose</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Frequency</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Start Date</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Period</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Unit</th>
        </tr>`;
      meds.forEach(m => {
        htmlString += `<tr>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.name || m.drug_name)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.dose)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.frequency)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${formatClinicalDate(m.start)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.period)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.periodUnit)}</td>
        </tr>`;
      });
      htmlString += `</table>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }

    htmlString += `<h4>Supportive Care</h4>`;
    const care = safeArray(tg.supportive_care);
    if (care.length) {
      htmlString += `<ul>${care.map(c => `<li>${c}</li>`).join("")}</ul>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }
  }

  // 6. Advices
  if (jsonPayload.advices) {
    const adv = jsonPayload.advices;
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Discharge Advices</h3>`;
    
    htmlString += `<h4>Medications To Continue</h4>`;
    const meds = safeArray(adv.medications_to_continue).filter(m => m && (m.name || m.drug_name || m.dose || m.start || m.period));
    if (meds.length) {
      htmlString += `<table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
        <tr style="background-color: #f2f2f2;">
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Name</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Dose</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Frequency</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Start Date</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Period</th>
          <th style="border: 1px solid #ddd; padding: 6px; text-align: left;">Unit</th>
        </tr>`;
      meds.forEach(m => {
        htmlString += `<tr>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.name || m.drug_name)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.dose)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.frequency)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${formatClinicalDate(m.start)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.period)}</td>
          <td style="border: 1px solid #ddd; padding: 6px;">${safeStr(m.periodUnit)}</td>
        </tr>`;
      });
      htmlString += `</table>`;
    } else {
      htmlString += `<p><em>None reported</em></p>`;
    }

    if (adv.diet) {
      const diets = safeArray(adv.diet.recommendations);
      htmlString += `<p><strong>Diet:</strong> ${safeStr(adv.diet.type)} ${diets.length > 0 ? "- " + diets.join(", ") : ""}</p>`;
    }

    const activity = safeArray(adv.activity_restrictions);
    if (activity.length) htmlString += `<p><strong>Activity Restrictions:</strong> ${activity.join(", ")}</p>`;

    const warnings = safeArray(adv.warning_signs);
    if (warnings.length) htmlString += `<p><strong>Warning Signs:</strong> ${warnings.join(", ")}</p>`;

    if (adv.follow_up) {
      htmlString += `<p><strong>Follow-up:</strong> ${safeStr(adv.follow_up.department)} in ${safeStr(adv.follow_up.after_days)} (Purpose: ${safeStr(adv.follow_up.purpose)})</p>`;
    }
  }

  // 7. Referral Details
  if (jsonPayload.referral_details) {
    const ref = jsonPayload.referral_details;
    htmlString += `<h3 style="color: #2c3e50; border-bottom: 1px solid #eee;">Referral Details</h3>`;
    htmlString += `<p><strong>Referred By:</strong> ${safeStr(ref.referred_by)} &nbsp; | &nbsp; <strong>Date:</strong> ${formatClinicalDate(ref.referral_date)}</p>`;
    htmlString += `<p><strong>Reason:</strong> ${safeStr(ref.reason_for_referral)}</p>`;
  }

  // Signatures
  if (isLamaDamaCase) {
    const ldd = jsonPayload.header?.lama_dama_details || {};
    htmlString += `<br/><br/>
      <div class="discharge-signatures-block" style="margin-top: 30px; margin-bottom: 20px; page-break-inside: avoid; break-inside: avoid;">
        <h4 style="color: #2c3e50; border-bottom: 1px solid #ccc; padding-bottom: 4px; margin-bottom: 15px;">Medico-Legal Signatures (${String(jsonPayload.header?.discharge_type || "").toUpperCase()})</h4>
        <table style="width: 100%; border-collapse: collapse; text-align: left; page-break-inside: avoid; break-inside: avoid;">
          <tr>
            <td style="width: 33%; vertical-align: top; padding: 8px;">
              <p style="margin: 0 0 4px 0;"><strong>Patient / Relative Signature:</strong></p>
              <p style="margin-top: 25px; margin-bottom: 6px;">_____________________________</p>
              <p style="font-size: 12px; margin: 2px 0;">Name: ${safeStr(ldd.surrogate_name)}</p>
              <p style="font-size: 12px; margin: 2px 0;">Relationship: ${safeStr(ldd.surrogate_relationship)}</p>
              <p style="font-size: 12px; margin: 2px 0;">Date/Time: _________________</p>
            </td>
            <td style="width: 33%; vertical-align: top; padding: 8px;">
              <p style="margin: 0 0 4px 0;"><strong>Treating Doctor Signature:</strong></p>
              <p style="margin-top: 25px; margin-bottom: 6px;">_____________________________</p>
              <p style="font-size: 12px; margin: 2px 0;">Name: ${safeStr(ldd.counseled_by || jsonPayload.header?.consultant_incharge || jsonPayload.prepared_by?.name)}</p>
              <p style="font-size: 12px; margin: 2px 0;">Reg. No: _________________</p>
              <p style="font-size: 12px; margin: 2px 0;">Date/Time: _________________</p>
            </td>
            <td style="width: 34%; vertical-align: top; padding: 8px;">
              <p style="margin: 0 0 4px 0;"><strong>Witness / Staff Nurse Signature:</strong></p>
              <p style="margin-top: 25px; margin-bottom: 6px;">_____________________________</p>
              <p style="font-size: 12px; margin: 2px 0;">Name: ${safeStr(jsonPayload.checked_by?.name || "Staff Nurse")}</p>
              <p style="font-size: 12px; margin: 2px 0;">Designation: Staff Nurse</p>
              <p style="font-size: 12px; margin: 2px 0;">Date/Time: _________________</p>
            </td>
          </tr>
        </table>
      </div>`;
  } else {
    htmlString += `<br/><br/>
      <div class="discharge-signatures-block" style="display: flex; justify-content: space-between; margin-top: 40px; margin-bottom: 20px; page-break-inside: avoid; break-inside: avoid;">
        <div>
          <p><strong>Prepared By:</strong> ${safeStr(jsonPayload.prepared_by?.name || "_________________")}</p>
          <p><small>${safeStr(jsonPayload.prepared_by?.designation)}</small></p>
        </div>
        <div>
          <p><strong>Checked By:</strong> ${safeStr(jsonPayload.checked_by?.name || "_________________")}</p>
          <p><small>${safeStr(jsonPayload.checked_by?.designation)}</small></p>
        </div>
      </div>`;
  }

  return htmlString;
};
